import { buildPdfAnnotationsHubAsync } from "../../API/pdf-annotations.hub.js";
import { sendUpdate } from "../../API/pdf-annotations.api.js";
import { createTaskStampImage, clearSelection, clearActivePage } from "../../Components/Call/Annotations.js";
import AnnotationType from "../../models/annotationType.js";
import * as Annotations from "../../Components/Call/Annotations.js";

function createPayload() {
	return {
		added: {},
		removedIds: new Set()
	};
}

const shapeTypeMap = {
	[AnnotationType.BlackLine]: `shapeAnnotation`,
	[AnnotationType.FreeText]: `freeTextAnnotation`,
	[AnnotationType.Image]: `stampAnnotations`,
	[AnnotationType.Stamp]: `stampAnnotations`,
	[AnnotationType.Ink]: `signatureInkAnnotation`,
};

const imageAnnotationTypes = [AnnotationType.Image, AnnotationType.Stamp];
const maxMessageSize = 20000;

function cannotProcessUpdate(pdfViewer) {
	return Annotations.isImageToolActive(pdfViewer) && Annotations.isImagePreviewActive(pdfViewer);
}

export class AnnotationsSync {
	namespaced = true;
	state() {
		return {
			updatePayload: createPayload(),
			changeTimeout: null,
			absoluteTimeout: null,
			changeDelay: window.Notary.Config.Conference.AnnotationChangeDelay,
			absoluteDelay: window.Notary.Config.Conference.AnnotationAbsoluteDelay,
			pdfViewer: null,
			hub: null,
			documentId: null,
			updatesQueue: [],
			updatesTimeout: null
		};
	}
	getters = {
		notarizationId: (_, __, rootState) => rootState.API.notarizationId
	}
	mutations = {
		addAnnotation(state, annotation) {
			if (annotation.customData?.isTask && !annotation.customData?.isComplete) {
				annotation.stampAnnotationPath = null;
			}
			state.updatePayload.added[annotation.annotationId] = annotation;
		},
		removeAnnotation(state, annotationId) {
			delete state.updatePayload.added[annotationId];
			state.updatePayload.removedIds.add(annotationId);
		},
		changeTimeout(state, timeoutId) {
			state.changeTimeout = timeoutId;
		},
		absoluteTimeout(state, timeoutId) {
			state.absoluteTimeout = timeoutId;
		},
		updatePayload(state, updatePayload) {
			state.updatePayload = updatePayload;
		},
		setPdfViewer(state, pdfViewer) {
			state.pdfViewer = pdfViewer;
		},
		setHub(state, hub) {
			state.hub = hub;
		},
		setDocumentId(state, id) {
			state.documentId = id;
		},
		updatesTimeout(state, timeoutId) {
			state.updatesTimeout = timeoutId;
		},
		enqueueUpdate(state, json) {
			state.updatesQueue.push(json);
		},
		shiftUpdatesQueue(state) {
			state.updatesQueue.shift();
		}
	}
	actions = {
		setTimeouts({ state, commit, dispatch }) {
			if (!state.absoluteTimeout) {
				commit(`absoluteTimeout`, setTimeout(() => dispatch(`sendUpdate`), state.absoluteDelay));
			}
			clearTimeout(state.changeTimeout);
			commit(`changeTimeout`, setTimeout(() => dispatch(`sendUpdate`), state.changeDelay));
		},
		syncAnnotationAdd({ commit, dispatch }, annotation) {
			dispatch(`setTimeouts`);
			commit(`addAnnotation`, annotation);
		},		
		syncAnnotationUpdate({ commit, dispatch }, annotation) {
			// If annotation is undefined or null, abort
			if(!annotation) return;
			dispatch(`setTimeouts`);
			commit(`removeAnnotation`, annotation.annotationId);
			commit(`addAnnotation`, annotation);
		},
		syncAnnotationRemove({ commit, dispatch }, annotationId) {
			dispatch(`setTimeouts`);
			commit(`removeAnnotation`, annotationId);
		},
		async sendUpdate({ state, getters, commit }) {
			clearTimeout(state.changeTimeout);
			clearTimeout(state.absoluteTimeout);
			commit(`absoluteTimeout`, null);
			const payload = state.updatePayload;
			commit(`updatePayload`, createPayload());
			payload.removedIds = [...payload.removedIds];
			const json = JSON.stringify(payload);
			if (json.length > maxMessageSize)
				await sendUpdate(getters.notarizationId, state.documentId, state.hub.connectionId, json);
			else
				await state.hub.invoke(`Update`, json);
		},
		receiveUpdate({ dispatch, state }, raw) {
			const json = JSON.parse(raw);
			if(state.updatesTimeout || cannotProcessUpdate(state.pdfViewer))
				dispatch(`enqueueUpdate`, json);
			else
				dispatch(`processUpdate`, json);
		},
		enqueueUpdate({state, commit, dispatch}, json) {
			if(!state.updatesTimeout) 			
				dispatch(this.setUpdatesTimeout);
			commit(`enqueueUpdate`, json);
		},
		setUpdatesTimeout({commit, dispatch}) {
			commit(`updatesTimeout`, setTimeout(() => dispatch(`processUpdates`), 500));
		},
		processUpdates({state, dispatch, commit}) {
			if(cannotProcessUpdate(state.pdfViewer)) {
				dispatch(this.setUpdatesTimeout);
				return;
			} 
			
			while(state.updatesQueue.length) {
				const update = state.updatesQueue[0];
				dispatch(`processUpdate`, update);
				commit(`shiftUpdatesQueue`);
			}
			commit(`updatesTimeout`, null);
		},		
		processUpdate({ state, rootState }, json) {
			json.removedIds?.forEach((annotationId) => {
				const renderedAnnotation = state.pdfViewer.annotations?.find(
					a => a.annotName === annotationId
				);
				if (renderedAnnotation) {
					// Based on Annotation.prototype.deleteAnnotation
					const pageNumber = renderedAnnotation.pageIndex;
					const selectedId = state.pdfViewer.selectedItems?.annotations[0]?.annotName;
					if (selectedId === annotationId) {
						clearSelection(state.pdfViewer);
					}
					const shapeType = renderedAnnotation.shapeAnnotationType;
					state.pdfViewer.annotation.updateImportAnnotationCollection(renderedAnnotation, pageNumber, shapeTypeMap[shapeType]);
					if (imageAnnotationTypes.includes(shapeType)) {
						state.pdfViewer.annotation.stampAnnotationModule.updateSessionStorage(renderedAnnotation, null, `delete`);
					} else {
						state.pdfViewer.annotation.modifyInCollections(renderedAnnotation, `delete`);
					}
					state.pdfViewer.annotation.updateAnnotationCollection(renderedAnnotation);
					state.pdfViewer.remove(renderedAnnotation);
				} else {
					const unrenderedAnnotation = state.pdfViewer.annotationCollection?.find(
						a => a.annotationId === annotationId
					);
					if (unrenderedAnnotation) {
						const shapeType = unrenderedAnnotation.shapeAnnotationType;
						const pageNumber = unrenderedAnnotation.pageNumber;
						state.pdfViewer.annotation.updateImportAnnotationCollection({ annotName: annotationId }, pageNumber, shapeTypeMap[shapeType]);
						state.pdfViewer.annotation.updateAnnotationCollection({ annotName: annotationId });
					}
				}
			});
			Object.values(json.added).forEach(async (annotation) => {
				if (annotation.author != rootState.author) {
					if (annotation.customData?.isTask) {
						annotation.annotationSettings.isLock = true;
						// needed for text annotations
						annotation.isReadonly = true;
						annotation.allowedInteractions = [`Select`, `Delete`, `Move`, `Resize`];
					} else {
						annotation.annotationSettings.isLock = true;
						annotation.allowedInteractions = [`None`];
					}
				}
				if (annotation.customData?.isTask && !annotation.customData?.isComplete) {
					const { color, initials, label } = annotation.customData.imageParameters;
					annotation.stampAnnotationPath = createTaskStampImage(color, initials, label).image;
				}
				state.pdfViewer.addAnnotation(annotation);
			});
		},
		async stopHub({state, dispatch}) {
			if (state.absoluteTimeout) {
				// send the queued updates
				await dispatch(`sendUpdate`);
			}
			state.hub?.stop();
		},
		async documentIdChanged({commit, dispatch}, documentId) {
			await dispatch(`stopHub`);
			commit(`setDocumentId`, documentId);
		},
		async documentLoaded({ state, commit, dispatch, getters }, isComplete) {
			if(isComplete) return;
			const hubConfig = {
				logLevel: window.Notary.Config.Hubs.LogLevel,
				onUpdate: (payload) => dispatch(`receiveUpdate`, payload),
				onCloseError: () => {
					//attempt to reconnect after 5 seconds
					setTimeout(async () => { await state.hub.start(); }, 5000);
				}
			};
			commit(`setHub`, await buildPdfAnnotationsHubAsync(hubConfig, getters.notarizationId, state.documentId));
			await state.hub.start();
		},
		resetActivePage({state}) {
			if(!state.pdfViewer) return;
			clearSelection(state.pdfViewer);
			clearActivePage(state.pdfViewer);
		}
	}
}